import { FC } from "react";
import { Spinner, Stack, Typography, NativeSelect } from "@altimateai/lego";
import { Chart, ChartTypes } from "@uicore";
import { Bar, Rectangle, TooltipProps } from "recharts";
import dayjs from "dayjs";
import { formatCost } from "@/helpers/utils";
import { CostChartTooltip } from "@/pages/query/Graph";
import { AGGREGATE_OPTIONS, AI_SERVICES_WITH_COLOR } from "./constants";
import type { AiServicesGraphResponse } from "./types";

interface Props {
  graphData?: AiServicesGraphResponse;
  isLoading: boolean;
  aggregateBy: string;
  onAggregateChange: (value: string) => void;
  currency: string;
}

export const AIServicesCostGraph: FC<Props> = ({
  graphData,
  isLoading,
  aggregateBy,
  onAggregateChange,
  currency,
}) => (
  <Stack direction="column" className="al-p-3 al-pb-10 al-border al-rounded-md al-mt-3">
    <Stack align="center" justify="between" className="al-w-full al-mb-2">
      <Typography variant="section-heading">Costs</Typography>
      <Stack align="center" gap="xs">
        <Typography variant="body">Aggregate by:</Typography>
        <NativeSelect
          size="sm"
          variant="ghost"
          value={aggregateBy}
          onChange={onAggregateChange}
          options={AGGREGATE_OPTIONS}
          buttonProps={{ "aria-label": "Aggregation level" }}
        />
      </Stack>
    </Stack>

    {isLoading ? (
      <Stack align="center" justify="center" gap="sm" className="al-w-full al-min-h-[300px]">
        <Spinner size="md" />
        Loading graph...
      </Stack>
    ) : graphData?.graph && graphData.graph.length > 0 ? (
      <Chart
        title=""
        xAxisDataKey="date"
        yAxisLabel={{ value: "Cost" }}
        xAxisLabelFormatter={(value: string) => dayjs.utc(value).format("DD MMM YY")}
        yAxisLabelFormatter={(value: string) => formatCost(value, currency)}
        type={ChartTypes.BarChart}
        data={graphData.graph}
        hideFullscreenButton
        tooltipProps={{
          content: (props: TooltipProps<number, string>) => (
            <CostChartTooltip
              {...props}
              aggregateBy={AGGREGATE_OPTIONS.find(o => o.value === aggregateBy)?.label}
            />
          ),
        }}
        chartProps={{}}
        legendProps={{ wrapperStyle: { bottom: -35 } }}
      >
        {({ barSize, selectedLegend }: { barSize: number; selectedLegend?: string }) => (
          <>
            {AI_SERVICES_WITH_COLOR.map((service, i) => (
              <Bar
                key={service.graphKey}
                barSize={Math.min(barSize, 60)}
                maxBarSize={60}
                dataKey={service.graphKey}
                name={service.displayName}
                fill={service.color}
                stackId="single"
                hide={Boolean(selectedLegend && selectedLegend !== service.graphKey)}
                shape={(props: unknown) => {
                  (props as { radius: number[] }).radius =
                    i === AI_SERVICES_WITH_COLOR.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0];
                  return <Rectangle {...(props as Record<string, unknown>)} />;
                }}
              />
            ))}
          </>
        )}
      </Chart>
    ) : (
      <Stack align="center" justify="center" className="al-w-full al-min-h-[200px]">
        <div className="al-text-light-gray al-text-center al-p-4">No data is present</div>
      </Stack>
    )}
  </Stack>
);
